import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Spinner } from "@/components/common";
import AppShell from "@/components/AppShell";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import { LoginPage, RegisterPage, ForgotPage, ResetPage } from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import NewTrip from "@/pages/NewTrip";
import TripWorkspace from "@/pages/TripWorkspace";
import InvitePage from "@/pages/Invite";
import Guide from "@/pages/Guide";
import GuestPortal from "@/pages/GuestPortal";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Notifications from "@/pages/Notifications";
import Help from "@/pages/Help";
import { Privacy, Terms } from "@/pages/Legal";

function AuthCallback() {
  const done = useRef(false);
  const nav = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/google/session", {}, { headers: { "X-Session-ID": sid } })
      .then((r) => {
        setUser(r.data);
        const next = sessionStorage.getItem("triply_next");
        sessionStorage.removeItem("triply_next");
        window.history.replaceState(null, "", window.location.pathname);
        nav(next || "/dashboard", { replace: true, state: { user: r.data } });
      })
      .catch(() => { setUser(false); nav("/login?error=google", { replace: true }); });
  }, [nav, setUser]);
  return <Spinner label="Signing you in…" />;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <AppShell>{children}</AppShell>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  const P = (el) => <Protected>{el}</Protected>;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPage />} />
      <Route path="/reset-password" element={<ResetPage />} />
      <Route path="/help" element={<Help />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/g/:slug" element={<Guide />} />
      <Route path="/guest/:token" element={<GuestPortal />} />
      <Route path="/invite/:token" element={<InvitePage kind="token" />} />
      <Route path="/join/:code" element={<InvitePage kind="code" />} />
      <Route path="/dashboard" element={P(<Dashboard />)} />
      <Route path="/trips/new" element={P(<NewTrip />)} />
      <Route path="/trips/:tripId/:tab?" element={P(<TripWorkspace />)} />
      <Route path="/settings" element={P(<Settings />)} />
      <Route path="/billing" element={P(<Billing />)} />
      <Route path="/payment/success" element={P(<PaymentSuccess />)} />
      <Route path="/notifications" element={P(<Notifications />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </AuthProvider>
  );
}
